import SectionHeader from "@/components/SectionHeader";

/**
 * "Welche Fotobox ist die beste?" — die Frage steht in der Search Console
 * gleich in mehreren Varianten und bereits auf Position 6 bis 9. Ihr fehlt
 * bislang nur eine Antwort.
 *
 * Statt einer Merkmalsliste, wie sie jeder Anbieter schreibt, steht hier bei
 * jedem Kriterium, wie viele der Boxen im Vergleich es erfüllen. Das macht
 * aus "achte auf gute Kamera" eine überprüfbare Aussage — und es zeigt, was
 * Standard ist und was ein echtes Unterscheidungsmerkmal.
 */

export default function AusstattungSektion({ merkmale }) {
  if (!merkmale) return null;

  const {
    anzahlBoxen,
    spiegelreflex,
    studioblitz,
    onlineGalerie,
    usb,
    handy,
    touch,
    liveView,
    drucklayouts,
  } = merkmale;

  const anteil = (n) => `${n} von ${anzahlBoxen}`;

  const KRITERIEN = [
    {
      titel: "Kamera",
      wert: anteil(spiegelreflex),
      zusatz: "mit Spiegelreflexkamera",
      text: "Der wichtigste Unterschied bei der Bildqualität. Spiegelreflex- und Systemkameras liefern auch bei wenig Licht scharfe Bilder — eine einfache HD-Webcam sieht man den Ausdrucken abends an.",
    },
    {
      titel: "Licht",
      wert: anteil(studioblitz),
      zusatz: "mit Studio-Blitz",
      text: "Ein Blitz friert Bewegung ein und leuchtet die Gesichter gleichmäßig aus. LED-Dauerlicht ist angenehmer für Videos, kommt bei dunklen Räumen aber schneller an seine Grenze.",
    },
    {
      titel: "Bilder danach",
      wert: anteil(onlineGalerie),
      zusatz: "mit Online-Galerie",
      text: `Der Ausdruck bleibt beim Gast, die Datei bei euch. ${anteil(handy)} schicken die Bilder direkt aufs Handy, ${anteil(usb)} liefern sie zusätzlich auf USB-Stick.`,
    },
    {
      titel: "Bedienung",
      wert: anteil(touch),
      zusatz: "mit Touchscreen",
      text: `Gäste sollen die Box ohne Erklärung bedienen können. ${anteil(liveView)} zeigen vor dem Auslösen eine Live-Vorschau — das reduziert Fehlversuche spürbar.`,
    },
    {
      titel: "Layout der Ausdrucke",
      wert: anteil(drucklayouts),
      zusatz: "mit wählbaren Layouts",
      text: "Vier Bilder im Streifen, ein großes Bild, eigenes Design mit Namen und Datum — bei vielen Anbietern lässt sich das Layout vorher festlegen.",
    },
  ];

  return (
    <SectionHeader
      id="ausstattung"
      title="Woran du eine gute Fotobox erkennst"
      subtitle="Fünf Kriterien — und wie viele der Fotoboxen im Vergleich sie erfüllen."
    >
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        <p>
          „Welche Fotobox ist die beste?" lässt sich nicht allgemein
          beantworten — wohl aber, worin sich die Angebote tatsächlich
          unterscheiden. Diese fünf Punkte entscheiden darüber, ob die Bilder am
          nächsten Morgen gut aussehen.
        </p>

        <dl className="mt-6 space-y-5">
          {KRITERIEN.map((k) => (
            <div
              key={k.titel}
              className="border-l-2 border-slate-200 pl-5"
            >
              <dt className="flex flex-wrap items-baseline gap-x-3">
                <span className="text-base font-semibold text-slate-900">
                  {k.titel}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#FF5744]">
                  {k.wert} {k.zusatz}
                </span>
              </dt>
              <dd className="mt-1 text-sm leading-6 text-slate-600">
                {k.text}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-6">
          Kamera und Licht siehst du auf jeder Karte im Vergleich als Bewertung,
          bevor du dich entscheidest — dieselben Kriterien für alle Anbieter,
          egal ob Versand oder vor Ort.
        </p>
      </div>
    </SectionHeader>
  );
}
